'use client'

import { useMemo } from 'react'
import type { LabelType, LabelWithRole } from '@skop-app/lib-domain/types'
import { sortBy } from 'lodash'
import { useTranslations } from 'next-intl'

import { useUserContext } from '@/context'
import { useSelectedTeammates } from '@/hooks/useSelectedTeammates'
import {
  buildDynamicOptionsLookup,
  buildDynamicRefData,
} from '@/hooks/gridOptionLookups'
import type {
  PlaceOptionGroup,
  PlacePickerOption,
} from '@/services/place/place.utils'
import { usePropertiesOptions } from '@/services/property/usePropertiesOptions'
import type { InventoryStatusCatalog } from '@/features/inventory/lib/inventory.statusActions'
import { usePlacePickerOptions, useProjects } from '@/services/queries'
import { useRefOptions } from '@/services/reference/reference.options'
import type { Language } from '@/types/translations'
import type { SupportedKeys } from '@/services/property'
import type { SelectOption } from '@/services/property/option.normalized'
import type {
  CustomSettings,
  DeletedOptionIdsByKey,
  DynamicFallbackLabels,
  OptionForDisplay,
  OptionLookup,
  ProjectLabel,
  RefDataByKey,
} from '@/types/grid'
import type {
  GroupedOptions,
  IndexedReference,
  Options,
  RefOptionsResult,
} from '@/services/reference/reference.type'

export type GridOptionsContext = {
  action: SelectOption[]
  optionsByID: Record<string, Record<string, OptionLookup>>
  refDataByKey: RefDataByKey
  deletedOptionIdsByKey?: DeletedOptionIdsByKey
  defaultsByKey: Partial<Record<string, LabelType>>
  refOptions: Options[] | GroupedOptions[]
  refDisplayOptions: Options[] | GroupedOptions[]
  referencesById: Record<string, IndexedReference>
  refreshReferenceOptions: RefOptionsResult['refreshReferenceOptions']
  definitionVersion: string | undefined
  isCustomProperty: boolean
  propertiesOptions: Record<string, OptionForDisplay[]>
  properties: SupportedKeys[]
  supportedKeys: SupportedKeys[]
  supportedKeysByKey: Record<string, SupportedKeys>
  selectOptionsByKey: Partial<Record<string, SelectOption[]>>
  displaySelectOptionsByKey: Partial<Record<string, SelectOption[]>>
  actionCatalog: InventoryStatusCatalog
  canCreateInListByKey: Partial<Record<string, boolean>>
  propertyOptions: Partial<Record<string, OptionForDisplay[]>>
  propertyDisplayOptions: Partial<Record<string, OptionForDisplay[]>>
  groupedPlaceID: PlaceOptionGroup[]
  placeID: PlacePickerOption[]
  projectID: ProjectLabel[]
  customSettings: CustomSettings
  byUserID: LabelWithRole[]
  organizationID: LabelType[]
  dynamicFallbackLabels: DynamicFallbackLabels
  isOrganizationView: boolean
  refKey: string
  isDone: boolean
  allProjects: ReadonlyArray<{
    value: string
    sections?: ReadonlyArray<Record<string, unknown>>
  }>
}

export const useGridOptions = ({
  propertyOptionsLang,
}: {
  propertyOptionsLang?: Language
} = {}): GridOptionsContext => {
  const t = useTranslations()
  const { customSettings, teams, isLoading: isLoadingUser } = useUserContext()
  const { data: teammates = [] } = useSelectedTeammates()

  const {
    properties,
    definitionVersion,
    isCustomProperty,
    supportedKeys,
    supportedKeysByKey,
    propertiesOptions,
    selectOptionsByKey,
    displaySelectOptionsByKey,
    actionCatalog,
    canCreateInListByKey,
    defaultsByKey,
    optionsByID,
    refDataByKey,
    deletedOptionIdsByKey,
    refKey,
    isLoading,
    isSuccess,
  } = usePropertiesOptions(propertyOptionsLang)

  const {
    refOptions,
    refDisplayOptions,
    referencesById,
    refreshReferenceOptions,
    isPending: isReferencesPending,
  } = useRefOptions()

  const { data: { placesOptions = [], groupedPlacesOptions = [] } = {} } =
    usePlacePickerOptions()
  const { data: allProjects = [] } = useProjects()

  const projects = useMemo(
    () =>
      sortBy(
        allProjects.map((project) => {
          const loc = project.location
          const locationStr =
            typeof loc === 'object' && loc && 'lat' in loc && 'lon' in loc
              ? `${loc.lat},${loc.lon}`
              : typeof loc === 'string'
                ? loc
                : undefined
          return {
            label: project.label,
            value: project.value,
            placeID: project.placeID,
            disabled: project.progress !== 'active',
            location: locationStr,
            type: project.type,
            preferences: project.preferences,
          }
        }),
        'label',
      ),
    [allProjects],
  )

  const dynamicRefData = useMemo<RefDataByKey>(
    () => ({
      byUserID: buildDynamicRefData(teammates),
      placeID: buildDynamicRefData(placesOptions),
      projectID: buildDynamicRefData(projects),
      organizationID: buildDynamicRefData(teams),
    }),
    [placesOptions, projects, teammates, teams],
  )

  const mergedRefDataByKey = useMemo<RefDataByKey>(
    () => ({
      ...refDataByKey,
      ...dynamicRefData,
    }),
    [dynamicRefData, refDataByKey],
  )

  const dynamicOptionsByID = useMemo<
    Record<string, Record<string, OptionLookup>>
  >(
    () => ({
      byUserID: buildDynamicOptionsLookup(teammates),
      placeID: buildDynamicOptionsLookup(placesOptions),
      projectID: buildDynamicOptionsLookup(projects),
      organizationID: buildDynamicOptionsLookup(teams),
    }),
    [placesOptions, projects, teammates, teams],
  )

  const optionsByIDForGrid = useMemo<
    Record<string, Record<string, OptionLookup>>
  >(
    () => ({
      ...dynamicOptionsByID,
      ...(optionsByID as Record<string, Record<string, OptionLookup>>),
    }),
    [dynamicOptionsByID, optionsByID],
  )

  const isDone = useMemo(
    () =>
      [isSuccess, !isLoading, !isLoadingUser, !isReferencesPending].every(
        Boolean,
      ),
    [isSuccess, isLoading, isLoadingUser, isReferencesPending],
  )

  const organizationID = useMemo(() => teams ?? [], [teams])

  const action = useMemo(
    () => selectOptionsByKey['action'] ?? [],
    [selectOptionsByKey],
  )

  const propertyOptions = useMemo<Partial<Record<string, OptionForDisplay[]>>>(
    () => ({
      ...propertiesOptions,
      ...Object.fromEntries(
        Object.entries(selectOptionsByKey).filter(([key]) => key !== 'action'),
      ),
    }),
    [propertiesOptions, selectOptionsByKey],
  )

  const propertyDisplayOptions = useMemo<
    Partial<Record<string, OptionForDisplay[]>>
  >(
    () => ({
      ...propertiesOptions,
      ...Object.fromEntries(
        Object.entries(displaySelectOptionsByKey).filter(
          ([key]) => key !== 'action',
        ),
      ),
    }),
    [displaySelectOptionsByKey, propertiesOptions],
  )

  const dynamicFallbackLabels = useMemo<DynamicFallbackLabels>(
    () => ({
      byUserID: t('Grid.dynamic-unresolved.byUserID'),
      organizationID: t('Grid.dynamic-unresolved.organizationID'),
      projectID: t('Grid.dynamic-unresolved.projectID'),
      placeID: t('Grid.dynamic-unresolved.placeID'),
      default: t('Grid.dynamic-unresolved.default'),
    }),
    [t],
  )

  return useMemo<GridOptionsContext>(
    () => ({
      propertyOptions,
      action,
      optionsByID: optionsByIDForGrid,
      refDataByKey: mergedRefDataByKey,
      deletedOptionIdsByKey,
      defaultsByKey,
      refOptions,
      refDisplayOptions,
      referencesById,
      refreshReferenceOptions,
      definitionVersion,
      isCustomProperty,
      propertiesOptions,
      properties,
      supportedKeys,
      supportedKeysByKey,
      selectOptionsByKey,
      displaySelectOptionsByKey,
      actionCatalog,
      canCreateInListByKey,
      propertyDisplayOptions,
      groupedPlaceID: groupedPlacesOptions,
      placeID: placesOptions,
      projectID: projects,
      customSettings,
      byUserID: teammates,
      organizationID,
      dynamicFallbackLabels,
      isOrganizationView: false,
      refKey,
      isDone,
      allProjects,
    }),
    [
      action,
      actionCatalog,
      allProjects,
      customSettings,
      defaultsByKey,
      definitionVersion,
      dynamicFallbackLabels,
      deletedOptionIdsByKey,
      isDone,
      isCustomProperty,
      mergedRefDataByKey,
      optionsByIDForGrid,
      organizationID,
      groupedPlacesOptions,
      placesOptions,
      propertyOptions,
      propertyDisplayOptions,
      projects,
      properties,
      propertiesOptions,
      refKey,
      refOptions,
      refDisplayOptions,
      referencesById,
      refreshReferenceOptions,
      selectOptionsByKey,
      displaySelectOptionsByKey,
      canCreateInListByKey,
      supportedKeys,
      supportedKeysByKey,
      teammates,
    ],
  )
}
